import { motion } from "framer-motion";
import { AlertTriangle, X, FileWarning, Scale, FileText } from "lucide-react";

const problems = [
  { icon: Scale, title: "Wrong entity structure", desc: "Investors won't even take the meeting." },
  { icon: FileWarning, title: "Unsigned co-founder agreement", desc: "A legal landmine the moment things get hard." },
  { icon: FileText, title: "Skipped IP assignment", desc: "Your company doesn't technically own its own product." },
  { icon: X, title: "Misunderstood term sheet", desc: "You get almost nothing in a decent exit." },
];

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5 } },
};

const ProblemSection = () => {
  return (
    <>
      <style>{`
        /* Scoped styles for Problem Section - Dark Theme */
        .ps-section {
          /* Locked at exactly 80px top and bottom */
          padding-top: 80px; 
          padding-bottom: 80px;
          padding-left: 1rem;
          padding-right: 1rem;
          background-color: #0b1120; /* Matching Founders/Chapters Dark Theme */
          overflow: hidden;
          width: 100%;
          display: block;
          box-sizing: border-box;
          position: relative;
        }

        .ps-container {
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
        }

        .ps-header {
          text-align: center;
          margin-bottom: 3.5rem;
        }

        .ps-title {
          font-size: 1.75rem;
          font-weight: 800;
          line-height: 1.2;
          margin: 0 0 1rem 0;
          color: #f8fafc; /* White text */
        }

        .ps-gradient {
          background: linear-gradient(to right, #2dd4bf, #22d3ee);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .ps-description {
          color: #94a3b8; /* Muted slate */
          font-size: 1rem;
          line-height: 1.6;
          max-width: 600px;
          margin: 0 auto;
        }

        .ps-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
          margin-bottom: 0;
        }

        /* Glassmorphism Cards */
        .ps-card {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 1.25rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          padding: 1.5rem;
          transition: all 0.3s ease;
          cursor: default;
        }

        .ps-card:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(45, 212, 191, 0.4);
          transform: translateY(-4px);
        }

        .ps-icon-box {
          width: 2.75rem;
          height: 2.75rem;
          border-radius: 0.75rem;
          background: rgba(45, 212, 191, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .ps-mistake-label {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #2dd4bf;
          margin-bottom: 4px;
          display: block;
        }

        .ps-card-title {
          font-weight: 700;
          font-size: 1.125rem;
          margin: 0 0 6px 0;
          color: #f1f5f9;
        }

        .ps-card-desc {
          font-size: 0.9rem;
          color: #94a3b8;
          margin: 0;
          line-height: 1.5;
        }

        /* Alert Box Dark Theme */
        .ps-alert {
          margin-top: 3.5rem;
          background: rgba(45, 212, 191, 0.05);
          border: 1px solid rgba(45, 212, 191, 0.2);
          border-radius: 1.25rem;
          padding: 1.75rem;
          display: flex;
          gap: 1.25rem;
          margin-bottom: 0; 
        }

        .ps-alert-title {
          font-weight: 700;
          color: #f8fafc;
          margin: 0 0 4px 0;
        }

        @media (min-width: 768px) {
          .ps-grid { grid-template-columns: 1fr 1fr; gap: 1.5rem; }
          .ps-title { font-size: 2.5rem; }
          .ps-card { padding: 2rem; }
          .ps-alert { padding: 2.25rem; }
        }

        @media (max-width: 480px) {
          .ps-alert { flex-direction: column; align-items: center; text-align: center; }
        }
      `}</style>

      <section className="ps-section">
        <div className="ps-container">
          <motion.div 
            className="ps-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="ps-title">
              Let's be honest about where you are{" "}
              <span className="ps-gradient">right now.</span>
            </h2>
            <p className="ps-description">
              You can build a product. You can find customers. But the legal and financial side? Most founders learn it by breaking things.
            </p>
          </motion.div>

          <motion.div 
            className="ps-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {problems.map((problem, i) => (
              <motion.div key={i} variants={itemVariants} className="ps-card">
                <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start' }}>
                  <div className="ps-icon-box">
                    <problem.icon size={22} color="#2dd4bf" />
                  </div>
                  <div>
                    <span className="ps-mistake-label">Mistake #{i + 1}</span>
                    <h3 className="ps-card-title">{problem.title}</h3>
                    <p className="ps-card-desc">{problem.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="ps-alert"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="ps-icon-box" style={{ background: 'rgba(45, 212, 191, 0.15)' }}>
               <AlertTriangle size={24} color="#2dd4bf" style={{ flexShrink: 0 }} />
            </div>
            <div>
              <p className="ps-alert-title">The real problem?</p>
              <p className="ps-card-desc">
                None of this is taught anywhere. And by the time most founders find out, it's already expensive to fix.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ProblemSection;